import { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { useAuthStore } from '../../stores/authStore';
import { getRoleHomePath, requiresOrganizationOnboarding } from '../../lib/onboarding';

export default function OrgOnboardingPage() {
  const navigate = useNavigate();
  const { user, fetchUser } = useAuthStore();
  const [organizationName, setOrganizationName] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const orgType = useMemo(() => {
    if (!user) return null;
    if (user.role === 'AGENCY') return 'AGENCY';
    if (user.role === 'BRAND') return 'BRAND';
    return null;
  }, [user]);

  // Must sit above the early returns below. Hooks have to run in the same order on every render,
  // and `user` starts null while the session loads — so a first render that bailed out early ran
  // one fewer hook than the render after `fetchUser` resolved, and React threw "Rendered more
  // hooks than during the previous render." The `!user || !orgType` guard reproduces exactly what
  // the early returns used to do: the effect body only ever ran when both were set.
  useEffect(() => {
    if (!user || !orgType) return;

    const draft = localStorage.getItem('onboarding:draftOrgName') || '';
    if (draft && !organizationName) {
      setOrganizationName(draft);
      localStorage.removeItem('onboarding:draftOrgName');
      return;
    }

    if (!organizationName) {
      const base = (user.fullName || user.email?.split('@')[0] || '').trim();
      if (base) {
        const suffix = orgType === 'AGENCY' ? ' Agency' : ' Brand';
        setOrganizationName(`${base}${suffix}`);
      }
    }
  }, [orgType, user, organizationName]);

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!requiresOrganizationOnboarding(user)) {
    return <Navigate to={getRoleHomePath(user)} replace />;
  }

  if (!orgType) {
    return <Navigate to={getRoleHomePath(user)} replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const name = organizationName.trim();
    if (name.length < 3) {
      setError('Organization name must be at least 3 characters');
      return;
    }

    try {
      setSubmitting(true);

      await api.post('/organizations', {
        name,
        type: orgType,
      });

      await fetchUser();
      navigate(getRoleHomePath(useAuthStore.getState().user), { replace: true });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to set up organization');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-xl bg-white/5 border border-white/10 rounded-2xl p-8">
        <h1 className="text-3xl font-bold mb-2">Complete your setup</h1>
        <p className="text-gray-300 mb-8">
          Create your {orgType === 'AGENCY' ? 'agency' : 'brand'} organization to unlock the dashboard.
        </p>

        {error && (
          <div className="mb-6 rounded-lg border border-red-400/40 bg-red-500/10 px-4 py-3 text-sm text-red-200">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-200 mb-2">Organization Name</label>
            <input
              value={organizationName}
              onChange={(e) => setOrganizationName(e.target.value)}
              placeholder={orgType === 'AGENCY' ? 'e.g. Stellar Agency' : 'e.g. Acme Brands Sdn Bhd'}
              className="w-full rounded-lg border border-white/20 bg-black/30 px-4 py-2.5 text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded-lg bg-gradient-to-r from-violet-600 to-blue-500 px-4 py-2.5 font-semibold disabled:opacity-60"
          >
            {submitting ? 'Setting up...' : 'Create organization and continue'}
          </button>
        </form>
      </div>
    </div>
  );
}
